/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppContext } from 'Context';
import { Page } from 'Components';
import { dataFetcher, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import RotatingLoader from 'Components/RotatingLoader';
import AddPlaylistModal from 'Components/AddPlaylistModal';
import AddAlbumModal from 'Components/AddAlbumModal';
import { ReactComponent as DragIcon } from 'Assets/drag.svg';
import AlterImage from 'Assets/alter-image.png';
import {
  Box, CircularProgress, Dialog, Zoom, FormControl, Select, MenuItem,
} from '@material-ui/core';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  VideoLibrary as AllVideosIcon,
  PhotoLibrary as PhotoIcon,
  PlaylistPlay as PlaylistIcon,
  Add as AddIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  Sort as SortIcon,
  Event as DateIcon,
  TextFields as TextIcon,
  ViewList as LayoutIcon,
  ArrowUpward,
  ArrowDownward,
  Cancel as CancelIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
} from '@material-ui/icons';
import { InfoModal } from '../dashboard/info-modal';
import CreateCategoryPage from '../content-layout/CreateCategory';
import { SquareCheckbox } from '../videos/components/CustomDesigns';
import VideoPanel from '../videos/components/VideoPanel';
import styles from './index.module.css';
import photoDeleteStyles from '../videos/components/AlbumPhotosView.module.css';

// Skeleton Card Component
const SkeletonCard = () => (
  <div className={styles.skeletonCard}>
    <div className={styles.skeletonThumbnail} />
    <div className={styles.skeletonInfo}>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonMeta} />
    </div>
  </div>
);

// Sidebar Skeleton Component
const SidebarSkeleton = () => (
  <div className={styles.sidebarSkeleton}>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className={styles.skeletonMenuItem}>
        <div className={styles.skeletonIcon} />
        <div className={styles.skeletonText} />
      </div>
    ))}
  </div>
);

// Content Item Card (base card without drag functionality)
const ContentItemCard = ({
  item,
  groupType,
  isDragging,
  isOverlay,
  isAllVideos,
  isSelected,
  onToggleSelect,
  viewMode = 'grid',
}) => {
  const thumbnail = groupType === '3'
    ? (item.imagekit_url || item.image || AlterImage)
    : (item.imagekit_icon || item.thumbnails?.[0] || item.thumbnail || AlterImage);

  const title = groupType === '3'
    ? (item.title || item.image?.split('/').pop() || '')
    : (item.title || 'Untitled');

  const stopDrag = (e) => e.stopPropagation();

  return (
    <div
      className={cn({
        [styles.contentCard]: true,
        [styles.listCard]: viewMode === 'list',
        [styles.dragging]: isDragging,
        [styles.overlay]: isOverlay,
        [styles.noDrag]: isAllVideos,
        [photoDeleteStyles.selectedCard]: isSelected,
      })}
    >
      <div className={styles.cardThumbnail}>
        <img
          src={thumbnail}
          alt={title}
          onError={(e) => { e.target.src = AlterImage; }}
        />
        {!isOverlay && onToggleSelect && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Select photo"
            aria-pressed={isSelected}
            className={photoDeleteStyles.checkboxWrap}
            onPointerDown={stopDrag}
            onMouseDown={stopDrag}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(item);
            }}
          >
            <SquareCheckbox selected={isSelected} onClick={() => {}} />
          </div>
        )}
        {!isAllVideos && (
          <div className={styles.dragHandle}>
            <DragIcon />
          </div>
        )}
      </div>
      <div className={styles.cardInfo}>
        <h3 className={styles.cardTitle}>{title}</h3>
        {groupType !== '3' && item.category && (
          <div className={styles.cardMeta}>{item.category}</div>
        )}
      </div>
    </div>
  );
};

// Sortable Item wrapper using @dnd-kit
const SortableItem = ({
  id, item, groupType, isAllVideos, isSelected, onToggleSelect, viewMode = 'grid',
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isAllVideos });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isAllVideos ? 'not-allowed' : 'grab',
    minWidth: 0, // Prevent item from expanding beyond grid cell
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={isAllVideos ? "This can't be reordered" : undefined}
      {...(isAllVideos ? {} : { ...attributes, ...listeners })}
    >
      <ContentItemCard
        item={item}
        groupType={groupType}
        isDragging={isDragging}
        isAllVideos={isAllVideos}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        viewMode={viewMode}
      />
    </div>
  );
};

// Sortable Sidebar Item wrapper using @dnd-kit
const SortableSidebarItem = ({
  id,
  item,
  isSelected,
  isPinned,
  onItemClick,
  getTypeIcon,
  defaultCategoryName,
  groupType,
  isDisabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  // Get background image for categories and albums
  const backgroundImage = (groupType === '1' || groupType === '3')
    ? (item.imagekit_icon || item.icon)
    : null;

  // Playlists get boxy style without background
  const isBoxyStyle = groupType === '2';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : isDisabled ? 0.6 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(styles.menuItem, {
        [styles.active]: isSelected,
        [styles.draggingSidebar]: isDragging,
        [styles.hasBackground]: !!backgroundImage,
        [styles.disabled]: isDisabled,
        [styles.boxyStyle]: isBoxyStyle,
      })}
      onClick={() => onItemClick(item)}
      role="button"
      tabIndex={0}
      {...attributes}
      {...listeners}
    >
      {backgroundImage && (
        <>
          <div
            className={styles.menuBackground}
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
          <div className={styles.menuShade} />
        </>
      )}
      <div className={styles.sidebarDragHandle}>
        <DragIcon />
      </div>
      {/* Keep the distinct "All Videos" icon as its type icon; other
          groups fall back to their folder/playlist/photo icon. */}
      {isPinned ? (
        <AllVideosIcon className={styles.menuIcon} />
      ) : (
        getTypeIcon(isSelected)
      )}
      <div className={styles.menuContent}>
        <span className={styles.menuLabel}>{item.title}</span>
      </div>
    </div>
  );
};

export const ContentLayoutDetails = () => {
  const { setAppSnackbar, langText, defaultCategoryName, userDetails } = useContext(AppContext);
  const navigate = useNavigate();
  const { layoutType } = useParams();
  const commonText = langText?.common;
  const layoutText = langText?.layout;

  const groupType = layoutType; // '1' = Categories, '2' = Playlists, '3' = Albums

  // Sidebar data (list of categories/playlists/albums)
  const [sidebarItems, setSidebarItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(true);

  // Content data (videos/photos in selected item)
  const [contentItems, setContentItems] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);

  // Grid vs list view toggle for the right-side content area
  const [viewMode, setViewMode] = useState('grid');

  // Sequential queue processing with reorderItem API
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarSaving, setIsSidebarSaving] = useState(false);
  const [isDraggingContent, setIsDraggingContent] = useState(false);
  const [isDraggingSidebar, setIsDraggingSidebar] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [sidebarQueueCount, setSidebarQueueCount] = useState(0);
  const reorderQueueRef = React.useRef([]);
  const sidebarReorderQueueRef = React.useRef([]);
  const isProcessingQueueRef = React.useRef(false);
  const isSidebarProcessingQueueRef = React.useRef(false);
  const reorderTimerRef = React.useRef(null);
  const sidebarReorderTimerRef = React.useRef(null);
  const DEBOUNCE_DELAY = 200; // 200ms delay - just enough to ensure user finished dragging

  // @dnd-kit: Active drag item for overlay
  const [activeId, setActiveId] = useState(null);
  const [activeSidebarId, setActiveSidebarId] = useState(null);

  // @dnd-kit: Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Infinite scroll state
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const itemsPerPage = 50;
  const contentContainerRef = React.useRef(null);

  // Sort state (mirrors /innerpage SortControls). 'layout' is the default
  // ordering returned by the API — no sort params sent in that case.
  const [sortBy, setSortBy] = useState('layout');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [isSorting, setIsSorting] = useState(false);

  // Quick reorder: persists the currently-applied sort as the saved layout
  // order via applyContentSort. Only meaningful when sortBy !== 'layout'.
  const [showApplySortModal, setShowApplySortModal] = useState(false);
  const [isApplyingSort, setIsApplyingSort] = useState(false);

  // Revert window: after a successful applyContentSort the backend snapshots
  // the previous order, so we swap the Quick Reorder button for a one-shot
  // "Revert" button (with a 30s countdown + manual close) tied to the
  // container that was just sorted. The snapshot is consumed on revert, so the
  // button hides after success / countdown / a second sort / item switch.
  const REVERT_WINDOW_SECONDS = 30;
  const [revertVisible, setRevertVisible] = useState(false);
  const [revertCountdown, setRevertCountdown] = useState(REVERT_WINDOW_SECONDS);
  const [isReverting, setIsReverting] = useState(false);
  const revertParentIdRef = React.useRef(null);
  const revertTimerRef = React.useRef(null);

  const clearRevertTimer = () => {
    if (revertTimerRef.current) {
      clearInterval(revertTimerRef.current);
      revertTimerRef.current = null;
    }
  };

  const hideRevert = () => {
    clearRevertTimer();
    setRevertVisible(false);
    revertParentIdRef.current = null;
  };

  // Start (or restart) the 30s revert window for the just-sorted container.
  const startRevertCountdown = (parentId) => {
    clearRevertTimer();
    revertParentIdRef.current = parentId;
    setRevertCountdown(REVERT_WINDOW_SECONDS);
    setRevertVisible(true);
    revertTimerRef.current = setInterval(() => {
      setRevertCountdown((prev) => {
        if (prev <= 1) {
          clearRevertTimer();
          setRevertVisible(false);
          revertParentIdRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Clear the timer if the component unmounts mid-countdown.
  useEffect(() => () => clearRevertTimer(), []);

  // Sidebar pagination state — load 20 at a time, append on scroll near
  // the bottom of the .menuList container, render a single shimmer row
  // below the last item while the next page is in flight.
  const SIDEBAR_PER_PAGE = 20;
  const [sidebarHasMore, setSidebarHasMore] = useState(true);
  const [sidebarLoadingMore, setSidebarLoadingMore] = useState(false);
  const sidebarListRef = React.useRef(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Multi-select + delete state — drives the selection panel and confirm
  // dialog for both album photos (groupType '3') and category videos
  // (groupType '1'). The UX mirrors the photo-album flow so both look
  // identical; only the delete endpoint differs (deleteVideo for category
  // videos, matching the /your-videos videos-tab bulk delete API).
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [photosPanelTransitionState, setPhotosPanelTransitionState] = useState('exited');

  // Multi-select is enabled for photo albums and category videos. Playlists
  // (groupType '2') still use the row-level remove button.
  const isMultiSelectEnabled = groupType === '3' || groupType === '1';
  const isVideoSelection = groupType === '1';
  const selectionLabel = isVideoSelection ? 'Video' : 'Photo';
  const selectionLabelPlural = isVideoSelection ? 'Videos' : 'Photos';

  const isPhotoSelected = (photoId) => selectedPhotos.some((p) => p?.id === photoId);
  const togglePhotoSelection = (photo) => {
    setSelectedPhotos((prev) => {
      const exists = prev.some((p) => p?.id === photo?.id);
      if (exists) return prev.filter((p) => p?.id !== photo?.id);
      return [...prev, photo];
    });
  };

  const requestBulkDelete = () => {
    if (selectedPhotos.length === 0) return;
    setPhotosToDelete(selectedPhotos);
  };

  const handleCancelPhotoDelete = () => {
    if (isDeletingPhoto) return;
    setPhotosToDelete([]);
  };

  const deleteCount = photosToDelete.length;
  const firstDeleteCandidate = photosToDelete[0];

  const handleConfirmPhotoDelete = async () => {
    if (photosToDelete.length === 0) return;
    setIsDeletingPhoto(true);
    try {
      let response;
      let removedItemIds;
      if (isVideoSelection) {
        // Match /your-videos videos-tab bulk delete: deleteDraftContent
        // expects two arrays — draft_id for in-progress drafts and content_id
        // for already-published videos (whose draft_id starts with 'old_video').
        const draftIds = [];
        const contentIds = [];
        photosToDelete.forEach((item) => {
          const isOldVideo = typeof item?.draft_id === 'string'
            && item.draft_id.startsWith('old_video');
          if (isOldVideo && item?.content_id) {
            contentIds.push(item.content_id);
          } else if (item?.draft_id) {
            draftIds.push(item.draft_id);
          } else if (item?.content_id) {
            contentIds.push(item.content_id);
          }
        });
        if (draftIds.length === 0 && contentIds.length === 0) return;
        response = await dataFetcher(endpoints.deleteDraftContent, {
          draft_id: draftIds,
          content_id: contentIds,
        });
        removedItemIds = photosToDelete.map((p) => p?.id).filter(Boolean);
      } else {
        const ids = photosToDelete.map((p) => p?.id).filter(Boolean);
        if (ids.length === 0) return;
        response = await dataFetcher(endpoints.deleteMyPhoto, { photo_ids: ids });
        removedItemIds = ids;
      }
      // deleteDraftContent signals success via `message === true`; deleteMyPhoto
      // returns the standard `success: true` flag. Accept either shape.
      const succeeded = response?.success === true || response?.message === true;
      if (succeeded) {
        const idSet = new Set(removedItemIds);
        setContentItems((prev) => prev.filter((it) => !idSet.has(it.id)));
        setTotalItems((prev) => Math.max(0, prev - removedItemIds.length));
        setSelectedPhotos((prev) => prev.filter((p) => !idSet.has(p?.id)));
        setAppSnackbar({
          isVisible: true,
          message: removedItemIds.length === 1
            ? `${selectionLabel} Removed`
            : `${removedItemIds.length} ${selectionLabelPlural} Removed`,
        });
        setPhotosToDelete([]);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message && response.message !== true
            ? response.message
            : `Oops! Something went wrong while removing ${selectionLabel.toLowerCase()}`,
        });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: isVideoSelection ? 'Delete Category Video' : 'Delete Album Photo',
        description: isVideoSelection ? 'Category video delete failed' : 'Album photo delete failed',
        severity: 'medium',
        errorMessage: err?.message || 'Delete failed',
        component: 'ContentLayoutDetails',
        userAction: isVideoSelection ? 'Click delete on category video' : 'Click delete on album photo',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: `Oops! Something went wrong while removing ${selectionLabel.toLowerCase()}`,
      });
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  // Get title based on type
  const getPageTitle = () => {
    if (groupType === '1') return layoutText?.categories || 'Categories';
    if (groupType === '2') return layoutText?.playlist || 'Playlists';
    return layoutText?.album || 'Photo Albums';
  };

  // Get icon based on type
  const getTypeIcon = (isSelected) => {
    if (groupType === '1') {
      return isSelected ? <FolderOpenIcon className={styles.menuIcon} /> : <FolderIcon className={styles.menuIcon} />;
    }
    if (groupType === '2') {
      return <PlaylistIcon className={styles.menuIcon} />;
    }
    return <PhotoIcon className={styles.menuIcon} />;
  };

  // Fetch sidebar items (categories/playlists/albums) with pagination.
  // preserveSelection: keep current selectedItem on first page (otherwise auto-select first item).
  // loadMore: true → append next page starting at current sidebarItems.length;
  //           false → fresh fetch starting at 0.
  const fetchSidebarItems = async (preserveSelection = false, loadMore = false) => {
    if (!groupType || !['1', '2', '3'].includes(groupType)) {
      setSidebarLoading(false);
      return;
    }

    // Guard duplicate load-more triggers (scroll listener can fire rapidly).
    if (loadMore && (sidebarLoadingMore || !sidebarHasMore)) return;

    if (loadMore) {
      setSidebarLoadingMore(true);
    } else {
      setSidebarLoading(true);
      setSidebarHasMore(true);
    }
    try {
      const endpoint = groupType === '1' ? endpoints.getMyCategory
        : groupType === '2' ? endpoints.getMyPlaylist
          : endpoints.getMyAlbum;

      const startOffset = loadMore ? sidebarItems.length : 0;
      // Albums sidebar shows newest-first (vpm: true → BE returns descending order_number).
      // Categories/playlists sidebar keep the original ascending order.
      const response = await dataFetcher(endpoint, {
        limit: SIDEBAR_PER_PAGE,
        start: startOffset,
        thumbnails: false,
        ...(groupType === '3' ? { vpm: true } : {}),
      });

      if (response?.success && Array.isArray(response?.data)) {
        const fetched = response.data;
        // "All Videos" is now freely reorderable, so honor the order the
        // backend returns instead of forcing the default category to top.
        const merged = loadMore ? [...sidebarItems, ...fetched] : fetched;
        setSidebarItems(merged);
        // No total count on these endpoints — infer end-of-list from a
        // short page (anything < limit means we hit the end).
        setSidebarHasMore(fetched.length === SIDEBAR_PER_PAGE);

        if (!loadMore) {
          // Preserve current selection or auto-select first item
          if (preserveSelection && selectedItem) {
            const updatedItem = merged.find((item) => item.id === selectedItem.id);
            if (updatedItem) {
              setSelectedItem(updatedItem);
            } else if (merged.length > 0) {
              setSelectedItem(merged[0]);
            }
          } else if (merged.length > 0) {
            setSelectedItem(merged[0]);
          }
        }
      } else if (loadMore) {
        // Empty/unsuccessful follow-up page — stop trying.
        setSidebarHasMore(false);
      }
    } catch (err) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to load items',
      });
      if (loadMore) setSidebarHasMore(false);
    }
    if (loadMore) {
      setSidebarLoadingMore(false);
    } else {
      setSidebarLoading(false);
    }
  };

  // Load sidebar items on mount (or when groupType switches)
  useEffect(() => {
    fetchSidebarItems();
  }, [groupType]);

  // Scroll-near-bottom → load next sidebar page. Same 200px window as the
  // content-area infinite scroll for consistency.
  const handleSidebarScroll = React.useCallback(() => {
    const container = sidebarListRef.current;
    if (!container) return;
    if (!sidebarHasMore || sidebarLoadingMore || sidebarLoading) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchSidebarItems(false, true);
    }
  }, [sidebarHasMore, sidebarLoadingMore, sidebarLoading, sidebarItems.length, groupType]);

  useEffect(() => {
    const container = sidebarListRef.current;
    if (!container) return undefined;
    container.addEventListener('scroll', handleSidebarScroll);
    return () => container.removeEventListener('scroll', handleSidebarScroll);
  }, [handleSidebarScroll]);

  // Fetch content for selected item with infinite scroll support
  const fetchContentForItem = async (item, loadMore = false) => {
    if (!item) return;

    // Prevent duplicate loading
    if (loadMore && loadingMore) return;
    if (loadMore && !hasMore) return;

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setContentLoading(true);
      setContentItems([]);
      setHasMore(true);
    }

    try {
      let response;
      // Calculate start offset based on current items (for load more)
      const startOffset = loadMore ? contentItems.length : 0;
      const paginationParams = {
        limit: itemsPerPage,
        start: startOffset,
      };

      // Sort params are appended only when a non-default sort is active —
      // matches /innerpage behavior so the default layout order is preserved.
      const sortParams = sortBy !== 'layout'
        ? { sort_by: sortBy, sort_order: sortOrder }
        : {};

      if (groupType === '1') {
        // Fetch videos for category using getCategoryDetail with pagination
        // vpm: false → BE returns videos in reversed (ascending order_number) order
        response = await dataFetcher(endpoints.getCategoryDetail, {
          user_id: userDetails?.myId,
          category_id: item.id,
          vpm: false,
          ...paginationParams,
          ...sortParams,
        });
      } else if (groupType === '2') {
        // Fetch videos for playlist (vpm: false → reversed, same as categories)
        response = await dataFetcher(endpoints.getMyPlaylist, {
          playlist_id: item.id,
          vpm: false,
          ...paginationParams,
          ...sortParams,
        });
      } else {
        // Fetch photos for album. getMyAlbumDetail uses a different param
        // shape than category/playlist: { sort_by: 'name', order_by: 0|1 }
        // where 0 → A-Z (ASC) and 1 → Z-A (DESC). Date sort is hidden in
        // the UI for albums, matching /your-videos albums tab.
        const albumSortParams = sortBy !== 'layout'
          ? { sort_by: sortBy, order_by: sortOrder === 'ASC' ? 0 : 1 }
          : {};
        response = await dataFetcher(endpoints.getMyAlbumDetail, {
          album_id: item.id,
          ...paginationParams,
          ...albumSortParams,
        });
      }

      if (response?.success) {
        // getCategoryDetail returns videos/video_count directly
        // getMyPlaylist and getMyAlbumDetail return data array
        let newItems;
        let total;
        if (groupType === '1') {
          // Category - getCategoryDetail returns data directly
          newItems = response?.videos || [];
          total = response?.video_count || 0;
        } else if (groupType === '3') {
          // Album - getMyAlbumDetail returns data array
          const itemData = response.data?.[0] || response.data;
          newItems = itemData?.photos || [];
          total = itemData?.photo_count || 0;
        } else {
          // Playlist - getMyPlaylist returns data array
          const itemData = response.data?.[0] || response.data;
          newItems = itemData?.videos || [];
          total = itemData?.video_count || 0;
        }

        setTotalItems(total);

        if (loadMore) {
          // Append new items to existing
          setContentItems((prev) => [...prev, ...newItems]);
        } else {
          setContentItems(newItems);
        }

        // Check if there are more items to load
        const loadedCount = loadMore ? contentItems.length + newItems.length : newItems.length;
        setHasMore(loadedCount < total);
      }
    } catch (err) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to load content',
      });
    }

    if (loadMore) {
      setLoadingMore(false);
    } else {
      setContentLoading(false);
    }
  };

  // Load content when selected item changes
  useEffect(() => {
    if (selectedItem) {
      fetchContentForItem(selectedItem);
    }
    // Clear photo selection whenever the user switches album/category in the sidebar.
    setSelectedPhotos([]);
    // The revert window is tied to the container that was just sorted — drop it
    // when the user navigates to a different item.
    if (revertVisible && revertParentIdRef.current !== selectedItem?.id) {
      hideRevert();
    }
  }, [selectedItem]);

  // Slide-in/out transition for the right-side photo selection panel.
  useEffect(() => {
    if (selectedPhotos.length > 0) {
      if (photosPanelTransitionState === 'exited') {
        setPhotosPanelTransitionState('entering');
        setTimeout(() => setPhotosPanelTransitionState('entered'), 50);
      }
    } else if (photosPanelTransitionState === 'entered') {
      setPhotosPanelTransitionState('exiting');
      setTimeout(() => setPhotosPanelTransitionState('exited'), 300);
    }
  }, [selectedPhotos.length, photosPanelTransitionState]);

  // Refetch when sort changes (categories, playlists, and albums). The
  // initial mount is harmless: selectedItem is null until the sidebar
  // loads, so the early-return below skips it; the selectedItem effect
  // handles the first fetch.
  useEffect(() => {
    if (!['1', '2', '3'].includes(groupType) || !selectedItem) return;
    setIsSorting(true);
    setHasMore(true);
    fetchContentForItem(selectedItem).finally(() => setIsSorting(false));
  }, [sortBy, sortOrder]);

  // Sort handler — matches /innerpage SortControls handleSort behavior.
  const handleSort = (value) => {
    const [type, orderValue] = value.split('-');
    if (type === 'layout') {
      if (sortBy === 'layout') return;
      setSortBy('layout');
      setSortOrder('DESC');
      return;
    }
    const newOrder = orderValue === '1' ? 'ASC' : 'DESC';
    if (sortBy === type && sortOrder === newOrder) return;
    setSortBy(type);
    setSortOrder(newOrder);
  };

  const handleClearSort = () => {
    if (sortBy === 'layout') return;
    setSortBy('layout');
    setSortOrder('DESC');
  };

  const sortValue = sortBy === 'layout'
    ? 'layout-0'
    : `${sortBy}-${sortOrder === 'ASC' ? '1' : '0'}`;

  // Map the UI sort state to the applyContentSort `sort_by` enum.
  // Returns null when the current sort can't be persisted (layout default).
  // NOTE: name/date keys are intentionally flipped — the BE treats them as the
  // opposite of their plain meaning (`name_desc` = A→Z, `date_oldest` = newest
  // first), so we send the inverse of the displayed sort direction here.
  const getApplySortKey = () => {
    if (sortBy === 'name') return sortOrder === 'ASC' ? 'name_asc' : 'name_desc';
    if (sortBy === 'date') return sortOrder === 'DESC' ? 'date_newest' : 'date_oldest';
    return null;
  };

  const handleApplyContentSort = async () => {
    const sortKey = getApplySortKey();
    if (!sortKey || !selectedItem?.id) return;
    setIsApplyingSort(true);
    try {
      const response = await dataFetcher(endpoints.applyContentSort, {
        type: groupType, // '1' for category — same as the route's :layoutType
        parent_id: selectedItem.id,
        sort_by: sortKey,
      });
      if (response?.success) {
        setAppSnackbar({ isVisible: true, message: 'Layout order updated' });
        setShowApplySortModal(false);
        // The saved layout order now matches the active sort. Drop back to
        // layout view so the user sees the persisted state and can drag-reorder.
        setSortBy('layout');
        setSortOrder('DESC');
        // Offer a one-shot revert for the next 30s (BE snapshotted the
        // previous order). Tied to the container we just sorted.
        startRevertCountdown(selectedItem.id);
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message || 'Failed to update layout order',
        });
      }
    } catch (err) {
      logAppEvent({
        userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
        functionAffected: 'Apply Content Sort',
        description: `applyContentSort failed for ${selectedItem?.id}`,
        severity: 'medium',
        errorMessage: err?.response?.data?.message || err?.message || 'applyContentSort failed',
        component: 'ContentLayoutDetails',
        userAction: 'Quick reorder confirm',
      });
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Failed to update layout order',
      });
    } finally {
      setIsApplyingSort(false);
    }
  };

  // One-shot revert of the most recent applyContentSort. Reuses the exact
  // type + encrypted parent_id from the sort call (token is auto-injected).
  const handleRevertContentSort = async () => {
    const parentId = revertParentIdRef.current || selectedItem?.id;
    if (!parentId || isReverting) return;
    setIsReverting(true);
    try {
      const response = await dataFetcher(endpoints.revertContentSort, {
        type: groupType,
        parent_id: parentId,
      });
      if (response?.success) {
        setAppSnackbar({ isVisible: true, message: 'Previous order restored' });
        hideRevert();
        // Snapshot is consumed — refresh the list so the restored order shows.
        setSortBy('layout');
        setSortOrder('DESC');
        if (selectedItem) {
          setHasMore(true);
          fetchContentForItem(selectedItem);
        }
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: response?.message || 'Failed to revert order',
        });
      }
    } catch (err) {
      // 404 → nothing to undo (snapshot already consumed). Hide silently.
      if (err?.response?.status === 404) {
        hideRevert();
      } else {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Revert Content Sort',
          description: `revertContentSort failed for ${parentId}`,
          severity: 'medium',
          errorMessage: err?.response?.data?.message || err?.message || 'revertContentSort failed',
          component: 'ContentLayoutDetails',
          userAction: 'Quick reorder revert',
        });
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: err?.response?.data?.message || 'Failed to revert order',
        });
      }
    } finally {
      setIsReverting(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = React.useCallback(() => {
    const container = contentContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls within 200px of bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (hasMore && !loadingMore && !contentLoading && selectedItem) {
        fetchContentForItem(selectedItem, true);
      }
    }
  }, [hasMore, loadingMore, contentLoading, selectedItem]);

  // Attach scroll listener
  useEffect(() => {
    const container = contentContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [handleScroll]);

  // Handle sidebar item click
  const handleSidebarItemClick = (item) => {
    // Prevent switching if sidebar is still loading
    if (sidebarLoading) {
      return;
    }

    // Prevent switching if content is still loading (showing shimmer)
    if (contentLoading) {
      return;
    }

    // Prevent switching if reorder queue is processing
    if (isSaving || isSidebarSaving) {
      setAppSnackbar({
        isVisible: true,
        type: 'warning',
        message: 'Please wait for reordering to complete'
      });
      return;
    }

    setSelectedItem(item);
    setHasMore(true);
    // Scroll to top when switching items
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
  };

  // The default "All Videos" category can't be reordered — drag, the sort
  // dropdown and Quick Reorder are all hidden for it. (Only relevant to
  // categories; playlists/albums have no such pseudo-item.)
  const isAllVideosCategory = groupType === '1' && selectedItem?.title === defaultCategoryName;

  // Get item ID for @dnd-kit - use item.id consistently for all types
  const getItemId = (item, idx) => (
    item.id || item.content_id || item.video_id || item.photo_id || `item-${idx}`
  );

  // @dnd-kit: Handle drag start
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setIsDraggingContent(true); // Disable sidebar immediately when dragging starts
  };

  // Sequential queue processor: Process reorder operations one by one
  const processContentReorderQueue = React.useCallback(async () => {
    if (isProcessingQueueRef.current || reorderQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    setIsSaving(true);

    const totalItems = reorderQueueRef.current.length;
    setQueueCount(totalItems);

    // Process each operation sequentially
    // eslint-disable-next-line no-await-in-loop
    while (reorderQueueRef.current.length > 0) {
      const operation = reorderQueueRef.current.shift(); // Get first item from queue
      setQueueCount(reorderQueueRef.current.length); // Update remaining count

      try {
        // BE precedence: after_id wins when both are sent, so only send one anchor.
        // before_id = "place item immediately before this item" (ascending order_number) —
        // needed when dropping at the bottom of a partially-loaded descending list.
        const payload = {
          type: operation.reorderType,
          item_id: operation.itemId,
          parent_id: operation.parentId,
        };
        if (operation.beforeId) {
          payload.before_id = operation.beforeId;
        } else {
          payload.after_id = operation.afterId;
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await dataFetcher(endpoints.reorderItem, payload);

        if (!response.success) {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
          break; // Stop processing on error
        }
      } catch (err) {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Sequential Reorder Content',
          description: 'Content reorder failed',
          severity: 'medium',
          errorMessage: err?.message || 'Reorder failed',
          component: 'ContentLayoutDetails',
          userAction: 'Sequential queue processing',
        });
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update order' });
        break; // Stop processing on error
      }
    }

    setQueueCount(0);
    setIsSaving(false);
    isProcessingQueueRef.current = false;
  }, [setAppSnackbar]);

  // Add reorder operation to queue
  const addToContentReorderQueue = React.useCallback((itemId, afterId, beforeId, parentId, reorderType) => {
    // Set saving state immediately to prevent sidebar from re-enabling between drag end and queue start
    setIsSaving(true);

    // Add to queue
    reorderQueueRef.current.push({ itemId, afterId, beforeId, parentId, reorderType });

    // Clear existing timer
    if (reorderTimerRef.current) {
      clearTimeout(reorderTimerRef.current);
    }

    // Start debounced queue processing
    reorderTimerRef.current = setTimeout(() => {
      processContentReorderQueue();
    }, DEBOUNCE_DELAY);
  }, [processContentReorderQueue]);

  // @dnd-kit: Handle drag end for reordering content
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    // Edge case: If dropped in same place or invalid drop, re-enable sidebar immediately
    if (!over || active.id === over.id) {
      setIsDraggingContent(false);
      return;
    }

    const oldIndex = contentItems.findIndex((item, idx) => getItemId(item, idx) === active.id);
    const newIndex = contentItems.findIndex((item, idx) => getItemId(item, idx) === over.id);

    // Edge case: Invalid indices, re-enable sidebar immediately
    if (oldIndex === -1 || newIndex === -1) {
      setIsDraggingContent(false);
      return;
    }

    const newItems = arrayMove(contentItems, oldIndex, newIndex);
    setContentItems(newItems);

    // Clear dragging flag - sidebar stays disabled during queue processing via isSaving state
    setIsDraggingContent(false);

    // Get the item that was moved and compute its anchor.
    // All groups now render ascending (order_number ASC): categories/playlists
    // via vpm: false, album photos natively. The ascending-order predecessor
    // (lower order_number) is therefore the item visually ABOVE the drop position.
    const movedItemId = active.id;
    const beforeId = null;
    const afterId = newIndex === 0 ? null : getItemId(newItems[newIndex - 1], newIndex - 1);

    // Determine the reorder type based on groupType
    const reorderType = groupType === '1' ? 'category_video'
      : groupType === '2' ? 'playlist_video'
        : 'album_photo';

    // Add to sequential queue
    addToContentReorderQueue(movedItemId, afterId, beforeId, selectedItem?.id, reorderType);
  };

  // Get active item for drag overlay
  const activeItem = activeId
    ? contentItems.find((item, idx) => getItemId(item, idx) === activeId)
    : null;

  // Sequential queue processor for sidebar: Process reorder operations one by one
  const processSidebarReorderQueue = React.useCallback(async () => {
    if (isSidebarProcessingQueueRef.current || sidebarReorderQueueRef.current.length === 0) {
      return;
    }

    isSidebarProcessingQueueRef.current = true;
    setIsSidebarSaving(true);

    const totalItems = sidebarReorderQueueRef.current.length;
    setSidebarQueueCount(totalItems);

    // Process each operation sequentially
    // eslint-disable-next-line no-await-in-loop
    while (sidebarReorderQueueRef.current.length > 0) {
      const operation = sidebarReorderQueueRef.current.shift(); // Get first item from queue
      setSidebarQueueCount(sidebarReorderQueueRef.current.length); // Update remaining count

      try {
        // Same anchor rule as the content queue: send exactly one of after_id/before_id.
        const payload = {
          type: operation.reorderType,
          item_id: operation.itemId,
        };
        if (operation.beforeId) {
          payload.before_id = operation.beforeId;
        } else {
          payload.after_id = operation.afterId;
        }
        // eslint-disable-next-line no-await-in-loop
        const response = await dataFetcher(endpoints.reorderItem, payload);

        if (!response.success) {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update sidebar order' });
          break; // Stop processing on error
        }
      } catch (err) {
        logAppEvent({
          userName: `${localStorage.getItem('firstname') || ''} ${localStorage.getItem('lastname') || ''}`.trim() || 'Guest',
          functionAffected: 'Sequential Reorder Sidebar',
          description: 'Sidebar reorder failed',
          severity: 'medium',
          errorMessage: err?.message || 'Sidebar reorder failed',
          component: 'ContentLayoutDetails',
          userAction: 'Sequential sidebar queue processing',
        });
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to update sidebar order' });
        break; // Stop processing on error
      }
    }

    setSidebarQueueCount(0);
    setIsSidebarSaving(false);
    isSidebarProcessingQueueRef.current = false;
  }, [setAppSnackbar]);

  // Add sidebar reorder operation to queue
  const addToSidebarReorderQueue = React.useCallback((itemId, afterId, beforeId, reorderType) => {
    // Set saving state immediately to prevent sidebar from re-enabling between drag end and queue start
    setIsSidebarSaving(true);

    // Add to queue
    sidebarReorderQueueRef.current.push({ itemId, afterId, beforeId, reorderType });

    // Clear existing timer
    if (sidebarReorderTimerRef.current) {
      clearTimeout(sidebarReorderTimerRef.current);
    }

    // Start debounced queue processing
    sidebarReorderTimerRef.current = setTimeout(() => {
      processSidebarReorderQueue();
    }, DEBOUNCE_DELAY);
  }, [processSidebarReorderQueue]);

  // Sidebar reorder handlers
  const handleSidebarDragStart = (event) => {
    setActiveSidebarId(event.active.id);
    setIsDraggingSidebar(true); // Disable sidebar items immediately when dragging starts
  };

  const handleSidebarDragEnd = (event) => {
    const { active, over } = event;
    setActiveSidebarId(null);

    // Edge case: If dropped in same place or invalid drop, re-enable sidebar immediately
    if (!over || active.id === over.id) {
      setIsDraggingSidebar(false);
      return;
    }

    const oldIndex = sidebarItems.findIndex((item) => item.id === active.id);
    const newIndex = sidebarItems.findIndex((item) => item.id === over.id);

    // Edge case: Invalid indices, re-enable sidebar immediately
    if (oldIndex === -1 || newIndex === -1) {
      setIsDraggingSidebar(false);
      return;
    }

    const newItems = arrayMove(sidebarItems, oldIndex, newIndex);
    setSidebarItems(newItems);

    // Clear dragging flag - sidebar stays disabled during queue processing via isSidebarSaving state
    setIsDraggingSidebar(false);

    // Get the item that was moved and compute its anchor.
    // Albums sidebar renders newest-first (descending order_number via vpm: true),
    // so the ascending-order predecessor is the item visually BELOW the drop position.
    // Categories/playlists sidebar still render ascending.
    const movedItemId = active.id;
    let afterId = null;
    let beforeId = null;
    if (groupType === '3') {
      const belowItem = newItems[newIndex + 1];
      if (belowItem) {
        afterId = belowItem.id;
      } else if (sidebarHasMore) {
        // Dropped at the bottom of the loaded page while older albums are still
        // unloaded — anchor on the item above instead: "place immediately before it".
        beforeId = newItems[newIndex - 1]?.id;
      }
      // else: dropped at the true bottom of the list → afterId stays null (first position)
    } else {
      afterId = newIndex === 0 ? null : newItems[newIndex - 1]?.id;
    }

    // Determine the reorder type based on groupType
    const reorderType = groupType === '1' ? 'category'
      : groupType === '2' ? 'playlist'
        : 'album';

    // Add to sequential queue
    addToSidebarReorderQueue(movedItemId, afterId, beforeId, reorderType);
  };

  // Get active sidebar item for drag overlay
  const activeSidebarItem = activeSidebarId
    ? sidebarItems.find((item) => item.id === activeSidebarId)
    : null;

  // Delete selected item
  const { mutate: deleteItem } = useSWR(
    showDeleteModal && selectedItem ? [
      groupType === '1' ? endpoints.deleteMyCategory
        : groupType === '2' ? endpoints.deleteMyPlayList
        : endpoints.deleteMyAlbum,
      selectedItem.id,
    ] : null,
    {
      fetcher: (url, id) => dataFetcher(url,
        groupType === '1' ? { catId: id }
          : groupType === '2' ? { id }
          : { album_id: id }),
      onSuccess: ({ success }) => {
        if (success) {
          setAppSnackbar({ isVisible: true, message: 'Deleted successfully' });
          setShowDeleteModal(false);
          setSelectedItem(null);
          fetchSidebarItems();
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to delete' });
        }
      },
      onError: () => {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to delete' });
      },
    }
  );

  // Get empty message
  const getEmptyMessage = () => {
    if (!selectedItem) {
      return `Select a ${groupType === '1' ? 'category' : groupType === '2' ? 'playlist' : 'album'} to view its contents`;
    }
    if (groupType === '3') {
      return 'No photos in this album';
    }
    return `No videos in this ${groupType === '1' ? 'category' : 'playlist'}`;
  };

  return (
    <Page className={styles.page} contentClassName={styles.pageContent} headerTheme={styles.headerTheme}>
      {/* Saving indicator */}
      {(isSaving || isSidebarSaving) && (
        <div className={styles.savingIndicator}>
          <CircularProgress size={14} style={{ color: 'white', marginRight: 8 }} />
          {queueCount > 0
            ? `Saving... (${queueCount} remaining)`
            : sidebarQueueCount > 0
              ? `Saving... (${sidebarQueueCount} remaining)`
              : 'Saving...'}
        </div>
      )}

      <div className={styles.mainLayout}>
        {/* Left Sidebar */}
        <div className={styles.leftSidebar}>
          {/* Sidebar Header */}
          <div className={styles.sidebarHeader}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <BackIcon style={{ width: 18, height: 18 }} />
              {commonText?.back || 'Back'}
            </button>
            <h2 className={styles.sidebarTitle}>{getPageTitle()}</h2>
          </div>

          {/* Sidebar Menu */}
          {sidebarLoading ? (
            <SidebarSkeleton />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleSidebarDragStart}
              onDragEnd={handleSidebarDragEnd}
            >
              <SortableContext
                items={sidebarItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.menuList} ref={sidebarListRef}>
                  {sidebarItems.map((item) => {
                    const isSelected = selectedItem?.id === item.id;
                    const isPinned = item.title === defaultCategoryName;
                    const isDisabled = sidebarLoading
                      || contentLoading
                      || isDraggingContent
                      || isDraggingSidebar
                      || isSaving
                      || isSidebarSaving;

                    return (
                      <SortableSidebarItem
                        key={item.id}
                        id={item.id}
                        item={item}
                        isSelected={isSelected}
                        isPinned={isPinned}
                        onItemClick={handleSidebarItemClick}
                        getTypeIcon={getTypeIcon}
                        defaultCategoryName={defaultCategoryName}
                        groupType={groupType}
                        isDisabled={isDisabled}
                      />
                    );
                  })}

                  {/* Single shimmer row matching the .menuItem pill shape
                      exactly (same padding, icon + label dimensions) so it
                      slots in as a believable placeholder for the next
                      category about to land. */}
                  {sidebarLoadingMore && (
                    <div className={styles.skeletonMenuItemPill} aria-hidden="true">
                      <div className={styles.skeletonIcon} />
                      <div className={styles.skeletonText} />
                    </div>
                  )}

                  {/* Add button is hidden until the user has scrolled to
                      the end of the list — showing it mid-list invites
                      the user to add when there may still be existing
                      items waiting to load. */}
                  {!sidebarHasMore && !sidebarLoadingMore && (
                    <button
                      className={styles.addItemButton}
                      onClick={() => setShowCreateModal(true)}
                    >
                      <AddIcon />
                      {groupType === '1' ? 'Add Category' : groupType === '2' ? 'Add Playlist' : 'Add Album'}
                    </button>
                  )}
                </div>
              </SortableContext>

              {/* Sidebar Drag Overlay */}
              <DragOverlay>
                {activeSidebarItem ? (
                  <div
                    className={cn(styles.menuItem, styles.overlay, {
                      [styles.hasBackground]: (groupType === '1' || groupType === '3') && (activeSidebarItem.imagekit_icon || activeSidebarItem.icon),
                      [styles.boxyStyle]: groupType === '2',
                    })}
                  >
                    {(groupType === '1' || groupType === '3') && (activeSidebarItem.imagekit_icon || activeSidebarItem.icon) && (
                      <>
                        <div
                          className={styles.menuBackground}
                          style={{ backgroundImage: `url(${activeSidebarItem.imagekit_icon || activeSidebarItem.icon})` }}
                        />
                        <div className={styles.menuShade} />
                      </>
                    )}
                    {activeSidebarItem.title === defaultCategoryName ? (
                      <AllVideosIcon className={styles.menuIcon} />
                    ) : (
                      getTypeIcon(false)
                    )}
                    <div className={styles.menuContent}>
                      <span className={styles.menuLabel}>{activeSidebarItem.title}</span>
                    </div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* Right Content Area */}
        <div className={styles.rightContent} ref={contentContainerRef}>
          {/* Content Header */}
          {selectedItem && (
            <div className={styles.contentHeader}>
              <h2 className={styles.contentTitle}>{selectedItem.title}</h2>
              <div className={styles.headerRight}>
                <div className={styles.viewToggle} role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={cn(styles.viewToggleBtn, {
                      [styles.viewToggleActive]: viewMode === 'grid',
                    })}
                    onClick={() => setViewMode('grid')}
                    aria-label="Grid view"
                    aria-pressed={viewMode === 'grid'}
                    title="Grid view"
                  >
                    <GridViewIcon style={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    className={cn(styles.viewToggleBtn, {
                      [styles.viewToggleActive]: viewMode === 'list',
                    })}
                    onClick={() => setViewMode('list')}
                    aria-label="List view"
                    aria-pressed={viewMode === 'list'}
                    title="List view"
                  >
                    <ListViewIcon style={{ fontSize: 18 }} />
                  </button>
                </div>
                {selectedItem.title !== defaultCategoryName && (
                  <div className={styles.contentActions}>
                    <button
                      className={styles.actionButton}
                      onClick={() => setShowEditModal(true)}
                    >
                      <EditIcon style={{ fontSize: 16, marginRight: 4 }} />
                      Edit
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.dangerButton}`}
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sort dropdown — categories, playlists, and albums. Style mirrors
              /your-videos video tab; options & API params mirror /innerpage.
              Albums omit Date Published (matches /your-videos albums tab when
              a specific album is selected). */}
          {['1', '2', '3'].includes(groupType) && selectedItem && !isAllVideosCategory && (contentItems.length > 0 || isSorting || contentLoading) && (
            <div className={styles.sortRow}>
              {/* During the 30s revert window the Quick Reorder button is
                  swapped for a one-shot Revert control (countdown + close). */}
              {revertVisible && revertParentIdRef.current === selectedItem?.id ? (
                <div className={styles.revertGroup}>
                  <button
                    type="button"
                    className={styles.revertButton}
                    onClick={handleRevertContentSort}
                    disabled={isReverting}
                    title="Undo the last sort and restore the previous order"
                  >
                    {isReverting
                      ? <CircularProgress size={16} style={{ color: '#fff', marginRight: 6 }} />
                      : <BackIcon style={{ fontSize: 18, marginRight: 6 }} />}
                    {isReverting ? 'Reverting...' : `Revert (${revertCountdown}s)`}
                  </button>
                  <button
                    type="button"
                    className={styles.revertClose}
                    onClick={hideRevert}
                    disabled={isReverting}
                    aria-label="Dismiss revert"
                    title="Dismiss"
                  >
                    <CancelIcon style={{ fontSize: 20 }} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.quickReorderButton}
                  onClick={() => setShowApplySortModal(true)}
                  disabled={sortBy === 'layout' || isSorting || contentLoading || isApplyingSort}
                  title={sortBy === 'layout'
                    ? 'Choose a sort option to save as default order'
                    : 'Save current sort as default layout order'}
                >
                  <LayoutIcon style={{ fontSize: 18, marginRight: 6 }} />
                  Quick Reorder
                </button>
              )}
              <div className={styles.droperSelection}>
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{
                    minWidth: 240,
                    backgroundColor: 'white',
                    borderRadius: 8,
                  }}
                >
                  <Select
                    value={sortValue}
                    onChange={(e) => handleSort(e.target.value)}
                    displayEmpty
                    disabled={isSorting || contentLoading}
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                    startAdornment={
                      <SortIcon style={{ marginRight: 8, fontSize: 18, color: '#666' }} />
                    }
                  >
                    <MenuItem value="layout-0">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                        <LayoutIcon style={{ fontSize: 18, color: '#666' }} />
                        <span className={styles.eachSortOpt}>Layout Order</span>
                      </div>
                    </MenuItem>
                    {groupType !== '3' && [
                      <MenuItem key="date-0" value="date-0">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                          <DateIcon style={{ fontSize: 18, color: '#666' }} />
                          <span className={styles.eachSortOpt}>Date Published</span>
                          <ArrowUpward style={{ fontSize: 16, color: '#4CAF50' }} />
                        </div>
                      </MenuItem>,
                      <MenuItem key="date-1" value="date-1">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                          <DateIcon style={{ fontSize: 18, color: '#666' }} />
                          <span className={styles.eachSortOpt}>Date Published</span>
                          <ArrowDownward style={{ fontSize: 16, color: '#FF9800' }} />
                        </div>
                      </MenuItem>,
                    ]}
                    <MenuItem value="name-1">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                        <TextIcon style={{ fontSize: 18, color: '#666' }} />
                        <span className={styles.eachSortOpt}>Name (A-Z)</span>
                        <ArrowUpward style={{ fontSize: 16, color: '#4CAF50' }} />
                      </div>
                    </MenuItem>
                    <MenuItem value="name-0">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                        <TextIcon style={{ fontSize: 18, color: '#666' }} />
                        <span className={styles.eachSortOpt}>Name (Z-A)</span>
                        <ArrowDownward style={{ fontSize: 16, color: '#FF9800' }} />
                      </div>
                    </MenuItem>
                  </Select>
                </FormControl>
                {sortBy !== 'layout' && (
                  <CancelIcon
                    style={{
                      cursor: 'pointer',
                      marginLeft: 8,
                      color: '#f44336',
                      fontSize: 24,
                      transition: 'color 0.2s',
                    }}
                    onClick={handleClearSort}
                    onMouseEnter={(e) => { e.target.style.color = '#d32f2f'; }}
                    onMouseLeave={(e) => { e.target.style.color = '#f44336'; }}
                  />
                )}
              </div>
            </div>
          )}

          <Box position="relative">
            {contentLoading ? (
              <div className={styles.loadingGrid}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : contentItems.length > 0 ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={contentItems.map((item, idx) => getItemId(item, idx))}
                    strategy={viewMode === 'list' ? verticalListSortingStrategy : rectSortingStrategy}
                  >
                    <div className={viewMode === 'list' ? styles.contentList : styles.contentGrid}>
                      {contentItems.map((item, idx) => {
                        const itemId = getItemId(item, idx);
                        return (
                          <SortableItem
                            key={itemId}
                            id={itemId}
                            item={item}
                            groupType={groupType}
                            isAllVideos={isAllVideosCategory}
                            isSelected={isMultiSelectEnabled && isPhotoSelected(item.id)}
                            onToggleSelect={isMultiSelectEnabled ? togglePhotoSelection : undefined}
                            viewMode={viewMode}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>

                  {/* Drag Overlay - shows smooth dragging preview */}
                  <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
                    {activeItem ? (
                      <ContentItemCard
                        item={activeItem}
                        groupType={groupType}
                        isDragging
                        isOverlay
                        viewMode={viewMode}
                      />
                    ) : null}
                  </DragOverlay>
                </DndContext>

                {/* Loading more indicator */}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <CircularProgress size={24} />
                    <span>Loading more...</span>
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMore && contentItems.length > 0 && (
                  <div className={styles.endOfList}>
                    {contentItems.length}
                    {' '}
                    {groupType === '3' ? 'photos' : 'videos'}
                    {' '}
                    loaded
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {groupType === '3' ? (
                    <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  ) : (
                    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  )}
                </svg>
                <p>{getEmptyMessage()}</p>
              </div>
            )}
          </Box>
        </div>

        {/* Right-side selection panel — appears when the user checks one or
            more photos/videos. Mirrors the YourVideos albums-tab UX. */}
        {isMultiSelectEnabled
          && (selectedPhotos.length > 0 || photosPanelTransitionState !== 'exited') && (
            <div
              className={cn({
                [photoDeleteStyles.photosPanelSlide]: true,
                [photoDeleteStyles[photosPanelTransitionState]]: true,
              })}
            >
              <VideoPanel
                itemType={isVideoSelection ? 'simpleVideo' : 'photo'}
                selectedItems={selectedPhotos}
                tabTitle={1}
                onClearSelection={() => setSelectedPhotos([])}
                onDeleteVideo={requestBulkDelete}
              />
            </div>
          )}
      </div>

      {/* Create Category Modal */}
      {showCreateModal && groupType === '1' && (
        <CreateCategoryPage
          initialOpen
          onModalClose={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Create Playlist Modal */}
      {showCreateModal && groupType === '2' && (
        <AddPlaylistModal
          open
          onClose={() => setShowCreateModal(false)}
          onPlaylistCreated={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Create Album Modal */}
      {showCreateModal && groupType === '3' && (
        <AddAlbumModal
          open
          onClose={() => setShowCreateModal(false)}
          onAlbumCreated={() => {
            setShowCreateModal(false);
            fetchSidebarItems();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <InfoModal
          isVisible={showDeleteModal}
          title="Are you sure you want to delete?"
          onHide={() => setShowDeleteModal(false)}
          continueButton="Go back"
          action={deleteItem}
          actionButton="Delete"
          isDelete
        />
      )}

      {/* Quick Reorder Confirmation Modal */}
      <Dialog
        open={showApplySortModal}
        TransitionComponent={Zoom}
        onClose={() => { if (!isApplyingSort) setShowApplySortModal(false); }}
        maxWidth="sm"
        fullWidth
        className={styles.applySortDialog}
      >
        <div className={styles.applySortHeader}>
          <div className={styles.applySortIconWrap}>
            <LayoutIcon className={styles.applySortIcon} />
          </div>
          <h2 className={styles.applySortTitle}>
            Save as Default Layout Order?
          </h2>
        </div>
        <div className={styles.applySortBody}>
          The current sort will become the saved order for
          {' '}
          <strong>{selectedItem?.title}</strong>
          . You can still drag-and-drop to fine-tune it afterwards.
          <div className={styles.applySortSummary}>
            {sortBy === 'name' && <TextIcon style={{ fontSize: 22, color: '#666' }} />}
            {sortBy === 'date' && <DateIcon style={{ fontSize: 22, color: '#666' }} />}
            <span>
              {sortBy === 'name'
                ? (sortOrder === 'ASC' ? 'Name (A-Z)' : 'Name (Z-A)')
                : 'Date Published'}
            </span>
            {sortBy === 'name' && (sortOrder === 'ASC'
              ? <ArrowUpward style={{ fontSize: 20, color: '#4CAF50' }} />
              : <ArrowDownward style={{ fontSize: 20, color: '#FF9800' }} />)}
            {sortBy === 'date' && (sortOrder === 'DESC'
              ? <ArrowUpward style={{ fontSize: 20, color: '#4CAF50' }} />
              : <ArrowDownward style={{ fontSize: 20, color: '#FF9800' }} />)}
          </div>
        </div>
        <div className={styles.applySortActions}>
          <button
            type="button"
            className={styles.applySortCancel}
            onClick={() => setShowApplySortModal(false)}
            disabled={isApplyingSort}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.applySortConfirm}
            onClick={handleApplyContentSort}
            disabled={isApplyingSort}
          >
            {isApplyingSort && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {isApplyingSort ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </Dialog>

      {/* Edit Category Modal - reuses CreateCategoryPage in edit mode */}
      {showEditModal && groupType === '1' && selectedItem && (
        <CreateCategoryPage
          initialOpen
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
            icon: selectedItem.icon_id || selectedItem.icon,
          }}
          onModalClose={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Edit Playlist Modal */}
      {showEditModal && groupType === '2' && selectedItem && (
        <AddPlaylistModal
          open
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
          }}
          onClose={() => setShowEditModal(false)}
          onPlaylistCreated={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Edit Album Modal */}
      {showEditModal && groupType === '3' && selectedItem && (
        <AddAlbumModal
          open
          editMode
          editData={{
            id: selectedItem.id,
            title: selectedItem.title,
          }}
          onClose={() => setShowEditModal(false)}
          onAlbumCreated={() => {
            setShowEditModal(false);
            fetchSidebarItems(true); // Preserve selection after edit
          }}
        />
      )}

      {/* Bulk Delete Confirmation Dialog — shared by album photos and
          category videos; labels and copy adapt to the selection type. */}
      <Dialog
        open={deleteCount > 0}
        TransitionComponent={Zoom}
        keepMounted
        aria-labelledby="delete-photo-dialog-title"
        aria-describedby="delete-photo-dialog-description"
        maxWidth="xs"
        fullWidth
        onClose={handleCancelPhotoDelete}
        PaperProps={{ className: photoDeleteStyles.dialogPaper }}
      >
        <div className={photoDeleteStyles.dialogContainer}>
          <div className={photoDeleteStyles.iconWrap}>
            <DeleteOutlineIcon />
          </div>
          <h3 id="delete-photo-dialog-title" className={photoDeleteStyles.dialogTitle}>
            {deleteCount > 1
              ? `Delete ${deleteCount} ${selectionLabelPlural}?`
              : `Delete ${selectionLabel}?`}
          </h3>
          <p id="delete-photo-dialog-description" className={photoDeleteStyles.dialogMessage}>
            {deleteCount > 1
              ? `Are you sure you want to delete these ${deleteCount} ${selectionLabelPlural.toLowerCase()}?`
              : (firstDeleteCandidate?.title
                ? `Are you sure you want to delete "${firstDeleteCandidate.title}"?`
                : `Are you sure you want to delete this ${selectionLabel.toLowerCase()}?`)}
            <br />
            This action cannot be undone.
          </p>
          <div className={photoDeleteStyles.actionContainer}>
            <button
              type="button"
              className={photoDeleteStyles.cancelBtn}
              onClick={handleCancelPhotoDelete}
              disabled={isDeletingPhoto}
            >
              Cancel
            </button>
            <button
              type="button"
              className={photoDeleteStyles.deleteBtn}
              onClick={handleConfirmPhotoDelete}
              disabled={isDeletingPhoto}
            >
              {isDeletingPhoto
                ? 'Deleting...'
                : (deleteCount > 1
                  ? `Delete ${deleteCount} ${selectionLabelPlural}`
                  : 'Delete')}
            </button>
          </div>
        </div>
      </Dialog>
    </Page>
  );
};
